import { IActionNodeActionData, ActionType } from "./IActionNodeAction"
import { useProxyWatch, Log, useTranslation } from "@fanfanlo"
import { Box, Stack, Typography, Switch, FormControlLabel, Select, MenuItem, TextField, FormControl, InputLabel } from "@mui/material"

const fileLog = new Log(false, 'NodeActionUI_f')
fileLog.pause = true

/**
 * NodeAction UI 组件
 * 提供配置界面让用户选择操作类型和相关参数
 */
export function UI(data: IActionNodeActionData) {
    fileLog.log('UI rendered, data =', data)

    const { t } = useTranslation('droid-project-v2-ext/actions/a11y/node-action/content')
    fileLog.log('i18n loaded')

    const [actionType] = useProxyWatch(data, "javaData.actionType", data.javaData.actionType)
    fileLog.log('actionType =', actionType)

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" component="b">
                    {t('UI.UI.title')}
                </Typography>
            </Box>

            <Stack direction="column" spacing={2}>
                {/* 操作类型显示 */}
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {t('UI.UI.actionTypeLabel')}:
                    </Typography>
                    <Typography variant="body1">
                        {getActionTypeLabel(actionType, t)}
                    </Typography>
                </Box>

                {/* 根据操作类型显示对应的配置 */}
                {actionType === ActionType.click && <ClickConfig data={data} />}
                {actionType === ActionType.scroll && <ScrollConfig data={data} />}
                {actionType === ActionType.setText && <SetTextConfig data={data} />}
                {actionType === ActionType.longClick && <LongClickConfig />}
                {actionType === ActionType.focus && <FocusConfig />}
            </Stack>
        </Box>
    )
}

/**
 * 点击操作配置
 */
function ClickConfig({ data }: { data: IActionNodeActionData }) {
    fileLog.log('ClickConfig rendered')

    const { t } = useTranslation('droid-project-v2-ext/actions/a11y/node-action/content')
    fileLog.log('ClickConfig i18n loaded')

    // 确保 clickConfig 存在
    if (!data.javaData.clickConfig) {
        fileLog.log('clickConfig is undefined, initializing...')
        data.javaData.clickConfig = {
            useGlobalClick: false,
            continuousClickConfig: {
                enableContinuousClick: false,
                baseInterval: 300,
                intervalVariation: 50
            }
        }
    }

    // 确保 continuousClickConfig 存在
    if (!data.javaData.clickConfig.continuousClickConfig) {
        fileLog.log('continuousClickConfig is undefined, initializing...')
        data.javaData.clickConfig.continuousClickConfig = {
            enableContinuousClick: false,
            baseInterval: 300,
            intervalVariation: 50
        }
    }

    const [useGlobalClick] = useProxyWatch(
        data.javaData.clickConfig,
        "useGlobalClick",
        data.javaData.clickConfig.useGlobalClick
    )

    const [enableContinuousClick] = useProxyWatch(
        data.javaData.clickConfig.continuousClickConfig,
        "enableContinuousClick",
        data.javaData.clickConfig.continuousClickConfig.enableContinuousClick
    )

    const [baseInterval] = useProxyWatch(
        data.javaData.clickConfig.continuousClickConfig,
        "baseInterval",
        data.javaData.clickConfig.continuousClickConfig.baseInterval
    )

    const [intervalVariation] = useProxyWatch(
        data.javaData.clickConfig.continuousClickConfig,
        "intervalVariation",
        data.javaData.clickConfig.continuousClickConfig.intervalVariation
    )

    fileLog.log('useGlobalClick =', useGlobalClick)
    fileLog.log('enableContinuousClick =', enableContinuousClick)
    fileLog.log('baseInterval =', baseInterval)
    fileLog.log('intervalVariation =', intervalVariation)

    function handleToggle(checked: boolean) {
        fileLog.log('handleToggle called, checked =', checked)
        if (data.javaData.clickConfig) {
            data.javaData.clickConfig.useGlobalClick = checked
        }
    }

    function handleContinuousToggle(checked: boolean) {
        fileLog.log('handleContinuousToggle called, checked =', checked)
        if (data.javaData.clickConfig?.continuousClickConfig) {
            data.javaData.clickConfig.continuousClickConfig.enableContinuousClick = checked
        }
    }

    function handleBaseIntervalChange(value: string) {
        fileLog.log('handleBaseIntervalChange called, value =', value)
        const numValue = parseInt(value, 10)
        fileLog.log('numValue =', numValue)

        if (!isNaN(numValue) && numValue >= 0) {
            if (data.javaData.clickConfig?.continuousClickConfig) {
                data.javaData.clickConfig.continuousClickConfig.baseInterval = numValue
            }
        } else {
            fileLog.log('handleBaseIntervalChange - invalid value, skipping')
        }
    }

    function handleIntervalVariationChange(value: string) {
        fileLog.log('handleIntervalVariationChange called, value =', value)
        const numValue = parseInt(value, 10)
        fileLog.log('numValue =', numValue)

        if (!isNaN(numValue) && numValue >= 0) {
            if (data.javaData.clickConfig?.continuousClickConfig) {
                data.javaData.clickConfig.continuousClickConfig.intervalVariation = numValue
            }
        } else {
            fileLog.log('handleIntervalVariationChange - invalid value, skipping')
        }
    }

    return (
        <Box>
            <FormControlLabel
                control={
                    <Switch
                        data-testid="NodeActionUI__useGlobalClickSwitch__01JFXYZK9A0B1C2D3E4F5G6H7"
                        checked={useGlobalClick}
                        onChange={(e) => handleToggle(e.target.checked)}
                    />
                }
                label={t('UI.UI.ClickConfig.useGlobalClickLabel')}
            />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {useGlobalClick
                    ? t('UI.UI.ClickConfig.useGlobalClickHintOn')
                    : t('UI.UI.ClickConfig.useGlobalClickHintOff')}
            </Typography>

            {/* 连续点击配置 */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <FormControlLabel
                    control={
                        <Switch
                            data-testid="NodeActionUI__enableContinuousClickSwitch__01JFXYZL9B1C2D3E4F5G6H7I8"
                            checked={enableContinuousClick}
                            onChange={(e) => handleContinuousToggle(e.target.checked)}
                        />
                    }
                    label={t('UI.UI.ClickConfig.enableContinuousClickLabel')}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {t('UI.UI.ClickConfig.enableContinuousClickHint')}
                </Typography>

                {enableContinuousClick && (
                    <Stack direction="column" spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            data-testid="NodeActionUI__baseIntervalInput__01JFXYZM9C2D3E4F5G6H7I8J9"
                            fullWidth
                            type="number"
                            label={t('UI.UI.ClickConfig.baseIntervalLabel')}
                            value={baseInterval}
                            onChange={(e) => handleBaseIntervalChange(e.target.value)}
                            inputProps={{ min: 0, step: 50 }}
                            helperText={t('UI.UI.ClickConfig.baseIntervalHint')}
                        />
                        <TextField
                            data-testid="NodeActionUI__intervalVariationInput__01JFXYZN9D3E4F5G6H7I8J9K0"
                            fullWidth
                            type="number"
                            label={t('UI.UI.ClickConfig.intervalVariationLabel')}
                            value={intervalVariation}
                            onChange={(e) => handleIntervalVariationChange(e.target.value)}
                            inputProps={{ min: 0, step: 10 }}
                            helperText={t('UI.UI.ClickConfig.intervalVariationHint', { baseInterval, intervalVariation })}
                        />
                    </Stack>
                )}
            </Box>
        </Box>
    )
}

/**
 * 滚动操作配置
 */
function ScrollConfig({ data }: { data: IActionNodeActionData }) {
    fileLog.log('ScrollConfig rendered')

    const { t } = useTranslation('droid-project-v2-ext/actions/a11y/node-action/content')
    fileLog.log('ScrollConfig i18n loaded')

    // 确保 scrollConfig 存在
    if (!data.javaData.scrollConfig) {
        fileLog.log('scrollConfig is undefined, initializing...')
        data.javaData.scrollConfig = { direction: 'forward' }
    }

    const [direction] = useProxyWatch(
        data.javaData.scrollConfig,
        "direction",
        data.javaData.scrollConfig.direction
    )

    fileLog.log('direction =', direction)

    function handleChange(newDirection: 'forward' | 'backward') {
        fileLog.log('handleChange called, newDirection =', newDirection)
        if (data.javaData.scrollConfig) {
            data.javaData.scrollConfig.direction = newDirection
        }
    }

    return (
        <Box>
            <FormControl fullWidth>
                <InputLabel>{t('UI.UI.ScrollConfig.directionLabel')}</InputLabel>
                <Select
                    data-testid="NodeActionUI__directionSelect__01JFXYZO9E4F5G6H7I8J9K0L1"
                    value={direction}
                    label={t('UI.UI.ScrollConfig.directionLabel')}
                    onChange={(e) => handleChange(e.target.value as 'forward' | 'backward')}
                >
                    <MenuItem value="forward">{t('UI.UI.ScrollConfig.directionForward')}</MenuItem>
                    <MenuItem value="backward">{t('UI.UI.ScrollConfig.directionBackward')}</MenuItem>
                </Select>
            </FormControl>
        </Box>
    )
}

/**
 * 设置文本操作配置
 */
function SetTextConfig({ data }: { data: IActionNodeActionData }) {
    fileLog.log('SetTextConfig rendered')

    const { t } = useTranslation('droid-project-v2-ext/actions/a11y/node-action/content')
    fileLog.log('SetTextConfig i18n loaded')

    // 确保 textConfig 存在
    if (!data.javaData.textConfig) {
        fileLog.log('textConfig is undefined, initializing...')
        data.javaData.textConfig = { text: '' }
    }

    const [text] = useProxyWatch(
        data.javaData.textConfig,
        "text",
        data.javaData.textConfig.text
    )

    fileLog.log('text =', text)

    function handleChange(newText: string) {
        fileLog.log('handleChange called, newText =', newText)
        if (data.javaData.textConfig) {
            data.javaData.textConfig.text = newText
        }
    }

    return (
        <Box>
            <TextField
                data-testid="NodeActionUI__setTextInput__01JFXYZP9F5G6H7I8J9K0L1M2"
                fullWidth
                label={t('UI.UI.SetTextConfig.textLabel')}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={t('UI.UI.SetTextConfig.textPlaceholder')}
            />
        </Box>
    )
}

/**
 * 长按操作说明
 */
function LongClickConfig() {
    const { t } = useTranslation('droid-project-v2-ext/actions/a11y/node-action/content')
    fileLog.log('LongClickConfig i18n loaded')

    return (
        <Box>
            <Typography variant="body2" color="text.secondary">
                {t('UI.UI.LongClickConfig.description')}
            </Typography>
        </Box>
    )
}

/**
 * 聚焦操作说明
 */
function FocusConfig() {
    const { t } = useTranslation('droid-project-v2-ext/actions/a11y/node-action/content')
    fileLog.log('FocusConfig i18n loaded')

    return (
        <Box>
            <Typography variant="body2" color="text.secondary">
                {t('UI.UI.FocusConfig.description')}
            </Typography>
        </Box>
    )
}

/**
 * 获取操作类型的标签
 */
function getActionTypeLabel(actionType: ActionType, t: (key: string, params?: any) => string): string {
    fileLog.log('getActionTypeLabel called, actionType =', actionType)

    const labels: Record<ActionType, string> = {
        [ActionType.click]: t('UI.UI.ActionType.click'),
        [ActionType.longClick]: t('UI.UI.ActionType.longClick'),
        [ActionType.scroll]: t('UI.UI.ActionType.scroll'),
        [ActionType.setText]: t('UI.UI.ActionType.setText'),
        [ActionType.focus]: t('UI.UI.ActionType.focus')
    }

    const label = labels[actionType]
    fileLog.log('getActionTypeLabel label =', label)

    return label || actionType
}
